import { ChangeDetectionStrategy, Component, computed, effect, input, linkedSignal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25LoadingInlineModule } from "../../../modules/s25-loading/s25.loading.inline.module";
import { S25ListDataComponent } from "../../../modules/s25-table-list/s25.list.data.component";
import { EventCopyOccurrencePayload } from "../../../pojo/ReservationI";
import { EventSummaryData } from "../../s25-event-summary/s25.event.summary.component";
import { CopyRelatedListEnhancer } from "./copy.related.list.enhancer";
import { EventCopyOccurrenceMappingComponent } from "./event.copy.occurrence.mapping.component";
import { EventCopyOptionsComponent } from "./event.copy.options.component";
import { ActionType, EventCopyEditContext, RoseEventCopyEditFeature } from "./rose.event.copy.edit.feature";

@TypeManagerDecorator("s25-ng-more-actions-edit")
@Component({
    selector: "s25-ng-more-actions-edit",
    standalone: true,
    imports: [
        S25ListDataComponent,
        S25LoadingInlineModule,
        EventCopyOccurrenceMappingComponent,
        EventCopyOptionsComponent,
    ],
    template: `
        @if (roseEdit.event.resource.isLoading()) {
            <s25-ng-loading-inline-static />
        }

        <div [class.copy-event-container]="type() === 'clone'">
            @if (roseEdit.event.resource.hasValue()) {
                <p class="moreActionsHeader">
                    <strong class="moreActionsEventName">{{ roseEdit.eventName() }}</strong>
                    {{ roseEdit.headerMsg() }}
                </p>
            }

            <div class="ngQuickSearchActions">
                @if (type() === "clone" && roseEdit.canRelateEvents()) {
                    <s25-ng-event-copy-options
                        [(isStandAlone)]="isStandAlone"
                        [hasRelationships]="!!roseEdit.relationships()?.length"
                    />
                }

                @if (type() === "clone" && eventId()) {
                    <s25-ng-event-copy-occurrence-mapping [eventId]="eventId()" [eventData]="eventData()" />
                }

                @if (roseEdit.relationships()?.length && roseEdit.listQuery()) {
                    <section class="copy-event-section">
                        <p class="copy-event-section__title">Related events</p>
                        <div class="moreActionsEditBox">
                            <s25-ng-list-data
                                #listComponent
                                [query]="roseEdit.listQuery()!"
                                [hasSelect]="true"
                                [enhancer]="copyListEnhancer"
                                [actions]="roseEdit.checkListActions()"
                                (selectedChange)="roseEdit.handleSelectedChange($event)"
                            />
                        </div>
                    </section>
                }

                @if (type() === "clone" && roseEdit.event.resource.hasValue()) {
                    <div class="copy-event-actions">
                        <button
                            id="event-copy-button"
                            class="aw-button aw-button--primary"
                            [disabled]="!canCopyEvent()"
                            (click)="currentCopyAction().fn()"
                        >
                            {{ currentCopyAction().text }}
                        </button>
                    </div>
                }

                @if (type() === "edit" && !roseEdit.relationships()?.length && roseEdit.event.resource.hasValue()) {
                    <div class="actionItem">
                        <p>{{ roseEdit.Buttons.edit.onlyCurrent.subText }}</p>
                        <div class="buttonContainer">
                            <button
                                id="event-edit-button"
                                class="aw-button aw-button--primary"
                                (click)="this.roseEdit.processCurrent()"
                            >
                                {{ roseEdit.Buttons.edit.onlyCurrent.text }}
                            </button>
                        </div>
                    </div>
                }
            </div>
        </div>
    `,
    host: {
        class: "moreActionsSection",
    },
    styleUrls: ["./more.actions.edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25MoreActionsEditComponent implements EventCopyEditContext {
    readonly eventId = input.required<number>();
    readonly type = input.required<ActionType>();
    readonly eventData = input<EventSummaryData>();
    readonly closeFn = input<() => void>();
    readonly omitListCloneActions = true;

    readonly listComponent = viewChild(S25ListDataComponent);
    readonly occurrenceMapping = viewChild(EventCopyOccurrenceMappingComponent);

    readonly copyListEnhancer = new CopyRelatedListEnhancer({
        showMappingStartDate: computed(() => this.type() === "clone"),
        dayOffset: computed(() => this.occurrenceMapping()?.dayOffset() ?? 0),
        readOnlyEventId: computed(() => this.eventId()),
        pinFirstItemId: computed(() => this.eventId()),
    });

    readonly roseEdit = new RoseEventCopyEditFeature(this);

    readonly canCopyEvent = computed(() => this.occurrenceMapping()?.canCopy() ?? true);
    readonly hasChecked = computed(() => this.roseEdit.checkedItemsSet().size > 0);
    readonly isStandAlone = linkedSignal<boolean | undefined>(() => !this.hasChecked());
    readonly copyAction = computed(() => {
        if (!this.isStandAlone() && this.roseEdit.canRelateEvents()) {
            return this.hasChecked() ? "copyEditSelected" : "copyAndRelate";
        }
        return "onlyCurrent";
    });
    readonly currentCopyAction = computed<{ text: string; fn: () => void }>(() => {
        if (this.copyAction() === "copyEditSelected") {
            return {
                text: this.roseEdit.Buttons.clone.actionOnSelected.text,
                fn: this.roseEdit.copySelected,
            };
        } else if (this.copyAction() === "copyAndRelate") {
            return {
                text: this.roseEdit.Buttons.clone.actionAndRelate.text,
                fn: this.roseEdit.copyAndRelateBack,
            };
        } else {
            return {
                text: this.roseEdit.Buttons.clone.onlyCurrent.text,
                fn: this.roseEdit.processCurrent,
            };
        }
    });

    constructor() {
        effect(() => {
            if (this.isStandAlone()) {
                this.listComponent()?.clearSelections();
            }
        });
    }

    close() {
        this.closeFn()?.();
    }

    getRoseOccurrencePayload(itemIds?: number[]): EventCopyOccurrencePayload | undefined {
        const payload = this.occurrenceMapping()?.getRoseOccurrencePayload(itemIds);
        const eventDayOffsets = this.copyListEnhancer.getEventDayOffsets();
        const hasEventOffsets = !!eventDayOffsets && Object.keys(eventDayOffsets).length > 0;
        if (!payload && !hasEventOffsets) {
            return undefined;
        }
        if (payload && !hasEventOffsets) {
            return payload;
        }
        return {
            segments: payload?.segments ?? [],
            dayOffset: payload?.dayOffset ?? 0,
            eventDayOffsets,
        };
    }
}
